import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DemoGenieService } from '@/services/demo/DemoGenieService';

/**
 * Hook for listing all demo agents
 */
export const useDemoGenieList = () => {
  return useQuery({
    queryKey: ["admin", "demo-genies"],
    queryFn: () => DemoGenieService.getActiveDemoAgents(),
    refetchInterval: 30_000,
  });
};

/**
 * Hook for provisioning stats
 */
export const useDemoGenieStats = () => {
  return useQuery({
    queryKey: ["admin", "demo-genie-stats"],
    queryFn: () => DemoGenieService.getProvisioningStats(),
    refetchInterval: 30_000,
  });
};

/**
 * Hook for bulk provisioning
 */
export const useBulkProvision = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (personaIds: string[]) => DemoGenieService.bulkProvision(personaIds),
    onSuccess: (result) => {
      const { success, failed } = result;
      toast.success(
        `Provisioned ${success.length} genies${failed.length > 0 ? `, ${failed.length} failed` : ""}`,
      );
      queryClient.invalidateQueries({ queryKey: ["admin", "demo-genies"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "demo-genie-stats"] });
    },
    onError: (error: Error) => {
      toast.error(`Bulk provisioning failed: ${error.message}`);
    },
  });
};

/**
 * Hook for provisioning by industry
 */
export const useBulkProvisionIndustry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (industryId: string) => DemoGenieService.bulkProvisionIndustry(industryId),
    onSuccess: (result) => {
      toast.success(`Provisioned ${result.success.length} genies for industry`);
      queryClient.invalidateQueries({ queryKey: ["admin", "demo-genies"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "demo-genie-stats"] });
    },
    onError: (error: Error) => {
      toast.error(`Industry provisioning failed: ${error.message}`);
    },
  });
};

/**
 * Hook for single genie provisioning
 */
export const useProvisionSingleGenie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (personaId: string) => DemoGenieService.getOrProvisionDemoAgent(personaId),
    onSuccess: () => {
      toast.success("Genie provisioned");
      queryClient.invalidateQueries({ queryKey: ["admin", "demo-genies"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "demo-genie-stats"] });
    },
    onError: (error: Error) => {
      toast.error(`Provisioning failed: ${error.message}`);
    },
  });
};

/**
 * Hook for deactivating a demo genie (sets is_active to false)
 */
export const useRetireDemoGenie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) =>
      // TODO call API
      null,
    onSuccess: () => {
      toast.success("Genie retired");
      queryClient.invalidateQueries({ queryKey: ["admin", "demo-genies"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "demo-genie-stats"] });
    },
  });
};

/**
 * Hook for re-embedding brand identity into all existing demo agents
 */
export const useReEmbedBranding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => DemoGenieService.reEmbedAllBranding(),
    onSuccess: (result) => {
      toast.success(
        `Branding re-embedded: ${result.updated} updated, ${result.failed} failed, ${result.skipped} skipped`,
      );
      queryClient.invalidateQueries({ queryKey: ["admin", "demo-genies"] });
    },
    onError: (error: Error) => {
      toast.error(`Re-embed branding failed: ${error.message}`);
    },
  });
};

/**
 * Hook for toggling active state of a demo agent
 */
export const useToggleDemoGenieActive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => null, // TODO call API to update is_active field
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "demo-genies"] });
    },
  });
};
